// Dữ liệu GIẢ để xem giao diện Nhật ký hệ thống khi backend chưa có API.
// Khi có API: thay các mảng dưới bằng repository, giữ nguyên hình dạng interface
// (hoặc chuyển interface sang src/models).

import moment from "moment";

// Mốc giờ tính lùi từ lúc mở trang, để "5 phút trước" và cột thời gian luôn khớp nhau.
const ago = (minutes: number) => moment().subtract(minutes, "minutes").format("YYYY-MM-DDTHH:mm:ss");

export type AccountRole = "admin" | "endUser";

export const ACCOUNT_ROLE_LABEL: Record<AccountRole, string> = {
  admin: "Admin",
  endUser: "Khách hàng",
};

export type AccessEvent =
  | "login"
  | "loginFailed"
  | "logout"
  | "changePassword"
  | "locked";

export const ACCESS_EVENT_LABEL: Record<AccessEvent, string> = {
  login: "Đăng nhập",
  loginFailed: "Đăng nhập thất bại",
  logout: "Đăng xuất",
  changePassword: "Đổi mật khẩu",
  locked: "Tài khoản bị khoá",
};

export interface AccessLog {
  id: number;
  accountId: number;
  accountName: string;
  accountContact: string;
  role: AccountRole;
  event: AccessEvent;
  isSuccess: boolean;
  /** Lý do thất bại, backend trả về. */
  reason?: string;
  /** Cảnh báo suy ra từ chuỗi lần sai liên tiếp, backend tính. */
  warning?: string;
  ip: string;
  device: string;
  client: string;
  createdAt: string;
}

export interface LoginSession {
  id: number;
  accountId: number;
  accountName: string;
  role: AccountRole;
  device: string;
  client: string;
  ip: string;
  startedAt: string;
  lastActiveAt: string;
  isCurrent?: boolean;
}

export type ActionObjectType = "device" | "group" | "scene" | "rule" | "hcl";

export const ACTION_OBJECT_LABEL: Record<ActionObjectType, string> = {
  device: "Thiết bị",
  group: "Nhóm",
  scene: "Cảnh",
  rule: "Kịch bản tự động",
  hcl: "HCL",
};

export const ACTION_OBJECT_ICON: Record<ActionObjectType, string> = {
  device: "tio-plug_outlined",
  group: "tio-layers_outlined",
  scene: "tio-movie",
  rule: "tio-autorenew",
  hcl: "tio-adaptive_lighting",
};

export type ActionSource = "app" | "auto" | "admin" | "system";

export const ACTION_SOURCE_LABEL: Record<ActionSource, string> = {
  app: "App",
  auto: "Tự động",
  admin: "Admin web",
  system: "Hệ thống",
};

export interface ActionLog {
  id: number;
  customerId: number;
  dormitoryId: number;
  objectType: ActionObjectType;
  objectName: string;
  roomName: string;
  action: string;
  /** Tác động mang trạng thái kết nối thì tô badge. */
  tone?: "success" | "error";
  source: ActionSource;
  actorName?: string;
  createdAt: string;
}

export interface LogDormitory {
  id: number;
  name: string;
}

export interface LogCustomer {
  id: number;
  name: string;
  phone: string;
  email: string;
  dormitories: LogDormitory[];
  homeControllerCount: number;
  actionCount7Days: number;
}

export const DEMO_CUSTOMERS: LogCustomer[] = [
  {
    id: 1,
    name: "Nguyễn Văn An",
    phone: "0903 456 789",
    email: "an.nguyen.thanxuan@gmail.com",
    dormitories: [
      { id: 11, name: "Nhà anh An – Thanh Xuân" },
      { id: 12, name: "Nhà nghỉ Sóc Sơn" },
    ],
    homeControllerCount: 2,
    actionCount7Days: 42,
  },
  {
    id: 2,
    name: "Trần Thị Mai",
    phone: "0912 345 678",
    email: "mai.tran@gmail.com",
    dormitories: [{ id: 21, name: "Nhà chị Mai – Cầu Giấy" }],
    homeControllerCount: 1,
    actionCount7Days: 1,
  },
  {
    id: 3,
    name: "Lê Quốc Bảo",
    phone: "0988 112 233",
    email: "bao.le@gmail.com",
    dormitories: [{ id: 31, name: "Biệt thự Ecopark B12" }],
    homeControllerCount: 1,
    actionCount7Days: 1,
  },
  {
    id: 4,
    name: "Hoàng Gia Huy",
    phone: "0965 870 112",
    email: "huy.hoang.giadinh.thanhxuan@gmail.com",
    dormitories: [{ id: 41, name: "Căn hộ Times City T5" }],
    homeControllerCount: 1,
    actionCount7Days: 0,
  },
];

const RECENT_ACCESS_LOGS: AccessLog[] = [
  { id: 1, accountId: 101, accountName: "Nguyễn Văn Hoàng", accountContact: "hoang.nv@rallismart.vn", role: "admin", event: "login", isSuccess: true, ip: "14.232.18.7", device: "macOS", client: "Chrome 129", createdAt: ago(7) },
  { id: 2, accountId: 2, accountName: "Trần Thị Mai", accountContact: "0912 345 678", role: "endUser", event: "login", isSuccess: true, ip: "113.161.42.90", device: "iPhone 15", client: "App iOS 2.3.1", createdAt: ago(21) },
  { id: 3, accountId: 3, accountName: "Lê Quốc Bảo", accountContact: "0988 112 233", role: "endUser", event: "locked", isSuccess: false, reason: "Tự khoá sau 5 lần sai mật khẩu", ip: "27.72.101.3", device: "Galaxy A54", client: "App Android 2.3.0", createdAt: ago(32) },
  { id: 4, accountId: 3, accountName: "Lê Quốc Bảo", accountContact: "0988 112 233", role: "endUser", event: "loginFailed", isSuccess: false, reason: "Sai mật khẩu", warning: "5 lần sai trong 3 phút", ip: "27.72.101.3", device: "Galaxy A54", client: "App Android 2.3.0", createdAt: ago(34) },
  { id: 5, accountId: 102, accountName: "Phạm Minh Đức", accountContact: "duc.pm@rallismart.vn", role: "admin", event: "changePassword", isSuccess: true, ip: "14.232.18.9", device: "Windows 11", client: "Edge 129", createdAt: ago(49) },
  { id: 6, accountId: 1, accountName: "Nguyễn Văn An", accountContact: "0903 456 789", role: "endUser", event: "logout", isSuccess: true, ip: "171.244.8.15", device: "Xiaomi 13T", client: "App Android 2.3.1", createdAt: ago(87) },
  { id: 7, accountId: 103, accountName: "Đỗ Thu Hà", accountContact: "ha.dt@rallismart.vn", role: "admin", event: "loginFailed", isSuccess: false, reason: "Sai mật khẩu", ip: "118.70.12.44", device: "Windows 10", client: "Chrome 129", createdAt: ago(118) },
  { id: 8, accountId: 4, accountName: "Hoàng Gia Huy", accountContact: "huy.hoang.giadinh.thanhxuan@gmail.com", role: "endUser", event: "login", isSuccess: true, ip: "42.118.230.7", device: "iPad Air", client: "App iOS 2.2.4", createdAt: ago(744) },
];

export const DEMO_SESSIONS: LoginSession[] = [
  { id: 1, accountId: 101, accountName: "Nguyễn Văn Hoàng", role: "admin", device: "macOS", client: "Chrome 129", ip: "14.232.18.7", startedAt: ago(7), lastActiveAt: ago(0), isCurrent: true },
  { id: 2, accountId: 2, accountName: "Trần Thị Mai", role: "endUser", device: "iPhone 15", client: "App iOS 2.3.1", ip: "113.161.42.90", startedAt: ago(21), lastActiveAt: ago(5) },
  { id: 3, accountId: 1, accountName: "Nguyễn Văn An", role: "endUser", device: "iPhone 13", client: "App iOS 2.3.1", ip: "171.244.8.20", startedAt: ago(4538), lastActiveAt: ago(12) },
  { id: 4, accountId: 102, accountName: "Phạm Minh Đức", role: "admin", device: "Windows 11", client: "Edge 129", ip: "14.232.18.9", startedAt: ago(105), lastActiveAt: ago(40) },
  { id: 5, accountId: 4, accountName: "Hoàng Gia Huy", role: "endUser", device: "iPad Air", client: "App iOS 2.2.4", ip: "42.118.230.7", startedAt: ago(744), lastActiveAt: ago(660) },
];

const RECENT_ACTION_LOGS: ActionLog[] = [
  { id: 1, customerId: 1, dormitoryId: 11, objectType: "device", objectName: "Đèn trần phòng khách", roomName: "Phòng khách", action: "Bật", source: "app", actorName: "Nguyễn Văn An", createdAt: ago(4) },
  { id: 2, customerId: 2, dormitoryId: 21, objectType: "device", objectName: "Ổ cắm phòng làm việc", roomName: "Phòng làm việc", action: "Tắt", source: "app", actorName: "Trần Thị Mai", createdAt: ago(6) },
  { id: 3, customerId: 1, dormitoryId: 11, objectType: "rule", objectName: "Về nhà buổi tối", roomName: "Cả nhà", action: "Kích hoạt", source: "auto", createdAt: ago(10) },
  { id: 4, customerId: 1, dormitoryId: 11, objectType: "scene", objectName: "Đi ngủ", roomName: "Phòng ngủ master", action: "Chạy cảnh", source: "app", actorName: "Trần Thị Lan", createdAt: ago(28) },
  { id: 5, customerId: 1, dormitoryId: 11, objectType: "device", objectName: "Cảm biến cửa chính", roomName: "Sảnh", action: "Mất kết nối", tone: "error", source: "system", createdAt: ago(42) },
  { id: 6, customerId: 3, dormitoryId: 31, objectType: "group", objectName: "Đèn sân vườn", roomName: "Sân", action: "Bật", source: "app", actorName: "Lê Quốc Bảo", createdAt: ago(55) },
  { id: 7, customerId: 1, dormitoryId: 11, objectType: "group", objectName: "Đèn tầng 1", roomName: "Tầng 1", action: "Tắt", source: "admin", actorName: "Nguyễn Văn Hoàng", createdAt: ago(59) },
  { id: 8, customerId: 1, dormitoryId: 12, objectType: "device", objectName: "Rèm phòng ngủ master (bên trái cửa sổ lớn)", roomName: "Phòng ngủ master", action: "Bật", source: "app", actorName: "Nguyễn Văn An", createdAt: ago(155) },
  { id: 9, customerId: 1, dormitoryId: 11, objectType: "hcl", objectName: "Ánh sáng sinh học phòng ngủ", roomName: "Phòng ngủ master", action: "Đổi chế độ", source: "auto", createdAt: ago(690) },
  { id: 10, customerId: 1, dormitoryId: 11, objectType: "device", objectName: "Cảm biến cửa chính", roomName: "Sảnh", action: "Kết nối lại", tone: "success", source: "system", createdAt: ago(938) },
];

// Lịch sử cũ hơn, sinh lặp để bảng có nhiều trang như dữ liệu thật.
const HISTORY_ACCOUNTS = [
  RECENT_ACCESS_LOGS[0], RECENT_ACCESS_LOGS[1], RECENT_ACCESS_LOGS[4], RECENT_ACCESS_LOGS[5], RECENT_ACCESS_LOGS[7],
];
const HISTORY_EVENTS: AccessEvent[] = ["login", "logout", "login", "login", "loginFailed", "logout"];

export const DEMO_ACCESS_LOGS: AccessLog[] = [
  ...RECENT_ACCESS_LOGS,
  ...Array.from({ length: 41 }, (_, index): AccessLog => {
    const account = HISTORY_ACCOUNTS[index % HISTORY_ACCOUNTS.length];
    const event = HISTORY_EVENTS[index % HISTORY_EVENTS.length];
    return {
      ...account,
      id: 100 + index,
      event,
      isSuccess: event !== "loginFailed",
      reason: event === "loginFailed" ? "Sai mật khẩu" : undefined,
      warning: undefined,
      createdAt: ago(24 * 60 + index * 197),
    };
  }),
];

const HISTORY_ACTIONS = RECENT_ACTION_LOGS.filter((item) => item.customerId === 1);

export const DEMO_ACTION_LOGS: ActionLog[] = [
  ...RECENT_ACTION_LOGS,
  ...Array.from({ length: 34 }, (_, index): ActionLog => ({
    ...HISTORY_ACTIONS[index % HISTORY_ACTIONS.length],
    id: 100 + index,
    createdAt: ago(26 * 60 + index * 173),
  })),
];
